import React, { useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import { password_validate } from "../../utils/helpers";
import { LuCheck, LuEye, LuEyeOff, LuX } from "react-icons/lu";
import { Button } from "../ui/button";

import { useNavigate } from "react-router-dom";
import "./change-password.css";

const PASSWORD_REQUIREMENTS = [
  {
    key: "length",
    label: "At least 8 characters",
    test: (value) => value.length >= 8,
  },
  {
    key: "uppercase",
    label: "At least one uppercase letter",
    test: (value) => /[A-Z]/.test(value),
  },
  {
    key: "lowercase",
    label: "At least one lowercase letter",
    test: (value) => /[a-z]/.test(value),
  },
  {
    key: "number",
    label: "At least one number",
    test: (value) => /\d/.test(value),
  },
];

const GEIST_FONT_STACK =
  '"Geist Local", Geist, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

function Change(props) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });
  const [saveErrors, setSaveErrors] = useState({});
  const [currentPasswordServerError, setCurrentPasswordServerError] =
    useState("");

  const {
    passwordChanged,
    changePassword,
    changePasswordError,
    embedded = false,
  } = props;

  const navigate = useNavigate();

  const onTextChange = (e) => {
    const { name, value } = e.target;
    if (name === "currentPassword") {
      setCurrentPasswordServerError("");
    }
    setSaveErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[name];
      return nextErrors;
    });
    if (name === "currentPassword") setCurrentPassword(value);
    else if (name === "newPassword") setNewPassword(value);
    else if (name === "confirmNewPassword") setConfirmNewPassword(value);
  };

  useEffect(() => {
    setCurrentPasswordServerError(changePasswordError || "");
  }, [changePasswordError]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (handleSaveError()) {
      const payload = {
        old_password: currentPassword,
        new_password: newPassword,
      };
      changePassword(payload);
    }
  };

  const handleSaveError = () => {
    const errors = {};
    let formIsValid = true;
    const hasValidNewPassword = PASSWORD_REQUIREMENTS.every((requirement) =>
      requirement.test(newPassword)
    );

    if (currentPassword === "") {
      formIsValid = false;
      errors["currentPassword"] = "Password can't be empty";
    }
    if (newPassword === "") {
      formIsValid = false;
      errors["newPassword"] = "New Password can't be empty";
    }
    if (
      currentPassword !== "" &&
      newPassword !== "" &&
      newPassword !== confirmNewPassword &&
      password_validate(newPassword) === true
    ) {
      formIsValid = false;
      errors["confirmNewPassword"] = "Passwords do not match";
    }
    if (newPassword !== "" && !hasValidNewPassword) {
      formIsValid = false;
      errors["newPassword"] =
        "Passwords must contain a minimum of eight characters, at least one uppercase letter, one lowercase letter, and a number";
    }

    setSaveErrors(errors);
    return formIsValid;
  };

  const toggleShow = (fieldName) => {
    setPasswordVisibility((currentVisibility) => ({
      ...currentVisibility,
      [fieldName]: !currentVisibility[fieldName],
    }));
  };

  useEffect(() => {
    if (passwordChanged) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_id");
      localStorage.removeItem("user_first_name");
      localStorage.removeItem("user_last_name");
      localStorage.removeItem("user_emails");
      localStorage.removeItem("user_default_email");
      localStorage.removeItem("selected_group");
      navigate("/login");
    }
  }, [passwordChanged, navigate]);

  return (
    <div
      style={{ fontFamily: GEIST_FONT_STACK }}
      className={
        embedded
          ? "change-password-page change-password--embedded"
          : "change-password-page"
      }>
      <Row className="align-items-center">
        <Col md={12}>
          <Row
            className={embedded ? "change-password__row" : undefined}
            style={{
              justifyContent: "center",
              alignItems: "center",
              marginTop: embedded ? 0 : "20vh",
            }}>
            <Col
              className={embedded ? "change-password__column" : undefined}
              md={5}
              lg={4}
              xl={3}
              sm={9}>
              <div
                className={
                  embedded
                    ? "change-password-card change-password__card"
                    : "change-password-card"
                }>
                <div className="password-form-heading">
                  <h2>Change password</h2>
                  <p>Update the password you use to access your account.</p>
                </div>
                <form onSubmit={handleSubmit}>
                  <fieldset className="password-form-section">
                    <PasswordField
                      error={
                        saveErrors.currentPassword || currentPasswordServerError
                      }
                      id="current-password"
                      label="Current password"
                      name="currentPassword"
                      onChange={onTextChange}
                      onToggleVisibility={() => toggleShow("currentPassword")}
                      type={
                        passwordVisibility.currentPassword
                          ? "text"
                          : "password"
                      }
                      value={currentPassword}
                      visible={passwordVisibility.currentPassword}
                    />
                  </fieldset>

                  <fieldset className="password-form-section">
                    <PasswordField
                      error={saveErrors.newPassword}
                      id="new-password"
                      label="New password"
                      name="newPassword"
                      onChange={onTextChange}
                      onToggleVisibility={() => toggleShow("newPassword")}
                      type={
                        passwordVisibility.newPassword ? "text" : "password"
                      }
                      value={newPassword}
                      visible={passwordVisibility.newPassword}
                    />
                    <PasswordField
                      error={saveErrors.confirmNewPassword}
                      id="confirm-new-password"
                      label="Confirm new password"
                      name="confirmNewPassword"
                      onChange={onTextChange}
                      onToggleVisibility={() =>
                        toggleShow("confirmNewPassword")
                      }
                      type={
                        passwordVisibility.confirmNewPassword
                          ? "text"
                          : "password"
                      }
                      value={confirmNewPassword}
                      visible={passwordVisibility.confirmNewPassword}
                    />
                    <ul
                      aria-label="Password requirements"
                      className="password-requirements">
                      {PASSWORD_REQUIREMENTS.map((requirement) => {
                        const isMet = requirement.test(newPassword);

                        return (
                          <li
                            className={isMet ? "is-met" : ""}
                            key={requirement.key}>
                            <span
                              aria-hidden="true"
                              className="password-requirement-marker">
                              {isMet ? <LuCheck /> : <LuX />}
                            </span>
                            {requirement.label}
                          </li>
                        );
                      })}
                    </ul>
                  </fieldset>
                  <Button
                    className="settings-password-submit"
                    size="lg"
                    type="submit"
                    variant="primary">
                    Change password
                  </Button>
                </form>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
}

function PasswordField({
  error,
  id,
  label,
  name,
  onChange,
  onToggleVisibility,
  type,
  value,
  visible,
}) {
  return (
    <div className={`password-field${error ? " password-field--error" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <div className="password-field__control">
        <input
          aria-invalid={Boolean(error)}
          id={id}
          name={name}
          onChange={onChange}
          type={type}
          value={value}
        />
        <button
          aria-label={
            visible
              ? `Hide ${label.toLowerCase()}`
              : `Show ${label.toLowerCase()}`
          }
          className="password-field__visibility"
          onClick={onToggleVisibility}
          type="button">
          {visible ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}
        </button>
      </div>
      {error && <span className="password-field__error">{error}</span>}
    </div>
  );
}

export default Change;
